
(() => {
  const drawer = document.getElementById('TvCart');
  if (!drawer) return;

  const root = (drawer.dataset.root || '/').replace(/\/?$/, '/');
  let busy = false;
  let returnFocus = null;
  let refreshTimer = null;

  const errorBox = () => drawer.querySelector('.tv-cart-error');

  function showError(error) {
    const box = errorBox();
    if (!box) return;
    box.textContent = error?.message || drawer.dataset.error ||
      'Er is iets misgelopen. Probeer opnieuw.';
    box.hidden = false;
  }

  function openDrawer() {
    if (!drawer.open) {
      returnFocus = document.activeElement;
      if (typeof drawer.showModal === 'function') {
        drawer.showModal();
      } else {
        drawer.setAttribute('open', '');
      }
    }
    document.body.classList.add('tv-cart-open');
  }

  function closeDrawer() {
    if (drawer.open) drawer.close();
  }

  drawer.addEventListener('close', () => {
    document.body.classList.remove('tv-cart-open');
    if (returnFocus?.isConnected) returnFocus.focus();
  });

  async function request(endpoint, body) {
    const options = {
      method: 'POST',
      headers: { Accept: 'application/json' }
    };

    if (body instanceof FormData) {
      options.body = body;
    } else {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    const response = await fetch(root + endpoint, options);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || drawer.dataset.error);
    }

    return data;
  }

  async function readCart() {
    const response = await fetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(
        drawer.dataset.error || 'Winkelmand niet beschikbaar'
      );
    }

    return response.json();
  }

  async function refreshDrawer() {
    const result = await request('cart/update.js', {
      sections: [drawer.dataset.section],
      sections_url: window.location.pathname
    });

    const html = result.sections?.[drawer.dataset.section];

    if (!html) {
      throw new Error('Kon de winkelmand niet bijwerken.');
    }

    const parsed = new DOMParser().parseFromString(
      html, 'text/html'
    );

    const next = parsed.getElementById('TvCart');
    const newContent = next?.querySelector('[data-cart-content]');
    const oldContent = drawer.querySelector('[data-cart-content]');

    if (!newContent || !oldContent) {
      throw new Error('Kon de winkelmand niet vernieuwen.');
    }

    const oldScroll = drawer.querySelector('.tv-cart-scroll');
    const scrollPosition = oldScroll?.scrollTop || 0;

    oldContent.replaceWith(newContent);

    const count = next.querySelector(
      '[data-drawer-count]'
    )?.textContent;

    const currentCount = drawer.querySelector(
      '[data-drawer-count]'
    );

    if (count != null) {
      if (currentCount) currentCount.textContent = count;

      document.querySelectorAll('.cart-link span').forEach(el => {
        el.textContent = count;
      });
    }

    const newScroll = drawer.querySelector('.tv-cart-scroll');
    if (newScroll) newScroll.scrollTop = scrollPosition;

    document.dispatchEvent(
      new CustomEvent('talvireo:cart-updated')
    );
  }

  async function updateAndOpen() {
    if (busy) return;

    try {
      await refreshDrawer();
      openDrawer();
    } catch (error) {
      openDrawer();
      showError(error);
    }
  }

  function scheduleOpen() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(updateAndOpen, 180);
  }

  async function runCartAction(action) {
    if (busy) return;
    busy = true;

    drawer.setAttribute('aria-busy', 'true');

    const box = errorBox();
    if (box) box.hidden = true;

    try {
      await action();
      await refreshDrawer();
    } finally {
      busy = false;
      drawer.removeAttribute('aria-busy');
    }
  }

  async function addItems(items) {
    await runCartAction(() =>
      request('cart/add.js', { items })
    );
    openDrawer();
  }

  async function addForm(form) {
    const button = form.querySelector('[type="submit"]');
    const previouslyDisabled = button?.disabled;

    if (button) button.disabled = true;

    try {
      await runCartAction(() =>
        request('cart/add.js', new FormData(form))
      );
      openDrawer();
    } catch (error) {
      openDrawer();
      showError(error);
    } finally {
      if (button?.isConnected) {
        button.disabled = previouslyDisabled;
      }
    }
  }

  // Publieke functies voor het Talvireo-thema.
  window.TalvireoCart = {
    add: addItems,
    open: updateAndOpen,
    refresh: refreshDrawer
  };

  // Bestaande winkelmandknoppen blijven werken.
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger) return;

    event.preventDefault();
    updateAndOpen();
  });

  // Sluiten van de winkelmand.
  drawer.addEventListener('click', event => {
    if (event.target.closest('[data-cart-close]')) {
      closeDrawer();
      return;
    }

    if (event.target === drawer) {
      const bounds = drawer.getBoundingClientRect();

      if (
        event.clientX < bounds.left ||
        event.clientX > bounds.right
      ) {
        closeDrawer();
      }
    }
  });

  // Eigenschappen van winkelmandregels vergelijken.
  function propertiesKey(properties) {
    return JSON.stringify(
      Object.entries(properties || {})
        .sort(([a], [b]) => a.localeCompare(b))
    );
  }

  // Aantal aanpassen zonder Sleek-kortingen zelf te veranderen.
  async function changeGroup(line, target) {
    const variantId = Number(line.dataset.variantId);
    const raw = JSON.parse(line.dataset.properties || '{}');

    const properties = Array.isArray(raw)
      ? Object.fromEntries(raw)
      : raw;

    const planId = Number(line.dataset.sellingPlan) || null;

    const matches = item =>
      item.variant_id === variantId &&
      propertiesKey(item.properties) === propertiesKey(properties) &&
      (item.selling_plan_allocation?.selling_plan?.id || null) === planId;

    let cart = await readCart();
    let lines = cart.items.filter(matches);
    let current = lines.reduce(
      (n, item) => n + item.quantity, 0
    );

    if (target > current) {
      const item = {
        id: variantId,
        quantity: target - current,
        properties
      };

      if (planId) item.selling_plan = planId;

      await request('cart/add.js', { items: [item] });
    } else {
      while (current > target && lines.length) {
        const item = lines[lines.length - 1];

        const quantity = Math.max(
          0,
          item.quantity - (current - target)
        );

        await request('cart/change.js', {
          id: item.key,
          quantity
        });

        cart = await readCart();
        lines = cart.items.filter(matches);

        const next = lines.reduce(
          (n, item) => n + item.quantity, 0
        );

        if (next >= current) {
          throw new Error('Aantal kon niet aangepast worden.');
        }

        current = next;
      }
    }
  }

  // Plus, min en verwijderen in de winkelmandlade.
  drawer.addEventListener('click', async event => {
    const button = event.target.closest(
      '[data-cart-step], [data-cart-remove]'
    );

    if (!button || busy) return;

    const line = button.closest('[data-line-key]');
    if (!line) return;

    const current = Number(line.dataset.quantity);
    const increment = Number(line.dataset.increment) || 1;
    const minimum = Number(line.dataset.min) || 1;

    const target = button.hasAttribute('data-cart-remove')
      ? 0
      : Math.max(
          0,
          current + Number(button.dataset.cartStep) * increment
        );

    const quantity = target === 0
      ? 0
      : Math.max(minimum, target);

    try {
      await runCartAction(() =>
        changeGroup(line, quantity)
      );
    } catch (error) {
      showError(error);
    }
  });

  // Gewone productformulieren buiten de aangepaste productsectie.
  document.addEventListener('submit', event => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const action = new URL(
      form.action, location.href
    ).pathname;

    if (!action.replace(/\/$/, '').endsWith('/cart/add')) {
      return;
    }

    event.preventDefault();

    if (busy || !form.reportValidity()) return;
    addForm(form);
  });

  // Sleek Bundles gebruikt de bestaande productknop.
  // We onderscheppen die knop NIET: Sleek blijft verantwoordelijk
  // voor de correcte aantallen en variantkeuzes.

  // Detecteer een geslaagde Shopify AJAX-toevoeging.
  // Open daarna de bestaande Talvireo-winkelmandlade.
  const originalFetch = window.fetch.bind(window);

  window.fetch = async function (...args) {
    const response = await originalFetch(...args);

    try {
      const input = args[0];
      const url = typeof input === 'string'
        ? input
        : input?.url || '';

      const path = new URL(url, location.href).pathname;
      const isCartAdd = /\/cart\/add(?:\.js)?\/?$/.test(path);

      const isOwnRequest =
        args[1]?.headers?.['X-Talvireo-Internal'];

      if (isCartAdd && response.ok && !isOwnRequest) {
        scheduleOpen();
      }
    } catch (error) {
      // De gewone Shopify-aanvraag blijft ongewijzigd.
    }

    return response;
  };

  // Ook na navigatie de winkelmand kunnen verversen.
  window.addEventListener('pageshow', () => {
    if (drawer.open && !busy) {
      refreshDrawer().catch(showError);
    }
  });

  // ============================================
  // TALVIREO KORTINGSVOORTGANG
  // 2 artikelen = 10%
  // 3 artikelen = 15%
  // 4+ artikelen = 20%
  // ============================================

  // Stijlen van de kortingsbalk.
  const progressStyle = document.createElement('style');

  progressStyle.textContent = `
    #TvCart .tv-discount-progress {
      padding: 18px 14px;
      margin: 0 0 16px;
      background: #F0F5EF;
      border: 1px solid #DBE7DD;
      border-radius: 13px;
      color: #173E35;
    }

    #TvCart .tv-discount-heading {
      font-size: 15px;
      font-weight: 700;
      line-height: 1.4;
      text-align: center;
      margin: 0 0 7px;
    }

    #TvCart .tv-discount-message {
      font-size: 12px;
      line-height: 1.6;
      text-align: center;
      color: #45665A;
      margin: 0 0 15px;
    }

    #TvCart .tv-discount-track,
    #TvCart .tv-discount-labels {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 7px;
    }

    #TvCart .tv-discount-step {
      height: 8px;
      border-radius: 20px;
      background: #D0DED3;
    }

    #TvCart .tv-discount-step.is-active {
      background: #245C49;
    }

    #TvCart .tv-discount-labels {
      margin-top: 9px;
      text-align: center;
    }

    #TvCart .tv-discount-label {
      font-size: 11px;
      color: #7B8D81;
    }

    #TvCart .tv-discount-label strong {
      display: block;
      font-size: 13px;
    }

    #TvCart .tv-discount-label.is-active {
      color: #245C49;
    }

    #TvCart .tv-discount-note {
      font-size: 10px;
      line-height: 1.5;
      color: #71847A;
      text-align: center;
      margin: 13px 0 0;
    }

    @media (max-width: 749px) {
      #TvCart .tv-discount-progress {
        padding: 16px 12px;
      }

      #TvCart .tv-discount-heading {
        font-size: 14px;
      }
    }
  `;

  document.head.appendChild(progressStyle);

  // Kortingsniveau bepalen op basis van aantal artikelen.
  function discountLevel(quantity) {
    if (quantity >= 4) return 20;
    if (quantity === 3) return 15;
    if (quantity === 2) return 10;
    return 0;
  }

  // HTML van de voortgangsbalk.
  function discountMarkup(quantity) {
    const level = discountLevel(quantity);

    const message = quantity >= 4
      ? 'Je hebt het hoogste kortingsniveau van 20% bereikt!'
      : `Voeg nog 1 artikel toe voor het kortingsniveau van ${
          quantity < 2 ? 10 : level + 5
        }%!`;

    const tiers = [
      { qty: 2, percent: 10 },
      { qty: 3, percent: 15 },
      { qty: 4, percent: 20 }
    ];

    const segments = tiers.map(tier => `
      <div class="tv-discount-step ${
        quantity >= tier.qty ? 'is-active' : ''
      }"></div>
    `).join('');

    const labels = tiers.map(tier => `
      <div class="tv-discount-label ${
        quantity >= tier.qty ? 'is-active' : ''
      }">
        <strong>${tier.percent}%</strong>
        ${tier.qty} artikelen
      </div>
    `).join('');

    const heading = level
      ? `Jouw kortingsniveau: ${level}%`
      : 'Meer producten, meer voordeel!';

    return `
      <div
        class="tv-discount-progress"
        aria-label="Talvireo kortingsvoortgang"
      >
        <h3 class="tv-discount-heading">
          ${heading}
        </h3>

        <p class="tv-discount-message">
          ${message}
        </p>

        <div
          class="tv-discount-track"
          aria-hidden="true"
        >
          ${segments}
        </div>

        <div class="tv-discount-labels">
          ${labels}
        </div>

        <p class="tv-discount-note">
          Kortingen zijn
          niet combineerbaar. De hoogste korting zal werken.
        </p>
      </div>
    `;
  }

  // Voortgangsbalk bijwerken.
  async function updateDiscountProgress() {
    try {
      const cart = await readCart();

      const content = drawer.querySelector(
        '[data-cart-content]'
      );

      if (!content) return;

      let wrapper = content.querySelector(
        '.tv-discount-progress-wrapper'
      );

      if (!cart.item_count) {
        if (wrapper) wrapper.remove();
        return;
      }

      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'tv-discount-progress-wrapper';
        content.prepend(wrapper);
      }

      wrapper.innerHTML = discountMarkup(
        cart.item_count
      );

    } catch (error) {
      console.warn(
        'Talvireo: kon kortingsbalk niet verversen.',
        error
      );
    }
  }

  // Automatisch bijwerken na cartwijzigingen.
  document.addEventListener(
    'talvireo:cart-updated',
    updateDiscountProgress
  );

  // Eerste weergave.
  updateDiscountProgress();

})();
